import { Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import { useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getField } from '/@/store/crop-monitoring/selectors';
import {
    setCompanyName as setCompanyNameAction,
    setPhone as setPhoneAction,
} from '/@/store/nitrogen-recommendation/actions';
import { getCompanyName, getPhone } from '/@/store/nitrogen-recommendation/selectors';
import { getSessionCountries, getSessionCountry } from '/@/store/session/selectors';
import AREA_CODES from '/@/utils/area-codes';
import { validatePhoneNumber } from '/@/utils/phone-numbers';
import stylesInput from '/@/v3/components/forms/input/styles.module.css';
import styles from './styles.module.css';

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography variant='body2' color='primary' sx={{ fontWeight: 600 }}>
        {children}
    </Typography>
);

const ContactStepper = () => {
    const dispatch = useDispatch();
    const companyName = useSelector(getCompanyName);
    const setCompanyName = bindActionCreators(setCompanyNameAction, dispatch);
    const phone = useSelector(getPhone);
    const setPhone = bindActionCreators(setPhoneAction, dispatch);
    const countries = useSelector(getSessionCountries);
    const country = useSelector(getSessionCountry);

    // Resolve the grower (productor) that owns the selected field to prefill their contact data.
    const selectedFieldId = useSelector(getField);
    const { data: selectedFieldData } = useFieldById({ id: selectedFieldId }, { enabled: !!selectedFieldId });
    const { data: grower } = useGetUserNode(selectedFieldData?.userNode as number);

    // Prefill the contact info from the grower, without clobbering anything the user
    // (or an edited recommendation) already set.
    // biome-ignore lint/correctness/useExhaustiveDependencies: action creators are stable; the guards prevent overwriting edits
    useEffect(() => {
        if (!grower) return;
        if (!companyName && grower.name) setCompanyName(grower.name);
        if (!phone && grower.phone) setPhone(grower.phone);
    }, [grower]);

    const phoneInvalid = !phone || !validatePhoneNumber(phone);

    return (
        <Grid
            className='mp-mask'
            container
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 4,
                mb: 4,
            }}
        >
            <Stack spacing={2}>
                <FieldLabel>{_('Productor')}</FieldLabel>
                <TextField
                    value={companyName || ''}
                    placeholder={_('Nombre del productor')}
                    onChange={(e) => setCompanyName(e.target.value)}
                    fullWidth
                />
            </Stack>

            <Stack spacing={2}>
                <FieldLabel>{_('Teléfono')} *</FieldLabel>
                <Stack spacing={0.5}>
                    <PhoneInput
                        onChange={setPhone}
                        placeholder={_('Número de teléfono')}
                        value={phone || ''}
                        buttonStyle={{ border: 'none' }}
                        specialLabel=''
                        country={country.code.toLowerCase()}
                        preferredCountries={countries.map((c) => c.code.toLowerCase())}
                        containerStyle={{ fontFamily: 'inherit', borderRadius: '4px' }}
                        inputClass={classNames(styles.phoneInput, stylesInput[`color-${settings.app.theme}`])}
                        containerClass={`${stylesInput[`color-${settings.app.theme}`]}`}
                        inputStyle={{
                            fontSize: '14px',
                            outline: 'none',
                            boxShadow: 'none',
                            borderColor: 'inherit',
                            width: '100%',
                        }}
                        enableAreaCodeStretch
                        enableAreaCodes
                        areaCodes={AREA_CODES}
                        masks={{ mx: '... .... ....' }}
                    />
                    {phoneInvalid && (
                        <Typography variant='caption' color='error'>
                            {phone
                                ? _('El formato del número de teléfono es incorrecto.')
                                : _('El teléfono es requerido')}
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Grid>
    );
};

export default ContactStepper;
